import * as React from 'react'
import { toastMultilineErrors } from '../../../util'
import { useState } from 'react'
import { validateStep, step1Schema } from '../../../validation/abstractSchema'
import { set } from 'zod'

const Step1 = ({ typeMappings, formData, setFormData, onNext, onBack }) => {
  const [selectedMode, setSelectedMode] = useState(formData.mode || '')
  const [selectedCategory, setSelectedCategory] = useState(
    formData.category || ''
  )
  const [selectedTopic, setSelectedTopic] = useState(formData.topic || '')

  // IUPAC Poster Award only
  const [posterAward, setPosterAward] = useState(formData.posterAward || {})

  // Get unique modes
  const modes = [
    ...new Map(
      typeMappings.map((m) => [m.abstractMode.id, m.abstractMode])
    ).values(),
  ]

  // Get categories based on selected mode
  const availableCategories =
    selectedMode !== ''
      ? [
          ...new Map(
            typeMappings
              .filter((m) => m.abstractMode.id === parseInt(selectedMode))
              .map((m) => [m.abstractCategory.id, m.abstractCategory])
          ).values(),
        ]
      : []

  // Get topics based on selected mode and category
  const availableTopics =
    selectedMode !== '' && selectedCategory !== ''
      ? [
          ...new Map(
            typeMappings
              .filter(
                (m) =>
                  m.abstractMode.id === parseInt(selectedMode) &&
                  m.abstractCategory.id === parseInt(selectedCategory)
              )
              .map((m) => [m.abstractTopic.id, m.abstractTopic])
          ).values(),
        ]
      : []

  const handleModeChange = (e) => {
    const value = e.target.value
    setSelectedMode(value)
    setSelectedCategory('')
    setSelectedTopic('')

    // Reset IUPAC Poster Award fields when mode changes
    setPosterAward({})
    
    setFormData({ ...formData, mode: value, category: '', topic: '', posterAward: {} })
  }

  const handleCategoryChange = (e) => {
    const value = e.target.value
    setSelectedCategory(value)
    setSelectedTopic('')
    setFormData({ ...formData, category: value, topic: '' })
  }

  const handleTopicChange = (e) => {
    const value = e.target.value
    setSelectedTopic(value)
    setFormData({ ...formData, topic: value })
  }

  const handleBack = () => {
    onBack()
  }

  const handleNext = () => {
    const stepData = {
      mode: selectedMode,
      category: selectedCategory,
      topic: selectedTopic,

      // IUPAC Poster Award only
      posterAward: {
        paParticipation: posterAward.paParticipation || false,
        paAge: posterAward.paAge || '',
        paProofOfIdentity: posterAward.paProofOfIdentity || null,
      },
    }

    console.log('Step1 validation data:', stepData)
    const validation = validateStep(stepData, step1Schema)
    console.log('Step1 validation result:', validation)

    if (!validation.isValid) {
      const errorMessages = Object.values(validation.errors)
      console.log('Step1 errors:', validation.errors)
      toastMultilineErrors(errorMessages)
      return
    }

    onNext()
  }

  // Temporarily disable for testing - will re-enable after validation works
  const isNextDisabled = false // !selectedMode || !selectedCategory || !selectedTopic

  return (
    <div className="relative w-full bg-white">
      <div className="flex flex-col items-start w-full space-y-10">
        {/* Abstract Type Section */}
        <div className="flex flex-col w-full space-y-2">
          <p className="text-lg font-bold leading-8 text-gray-900">
            Abstract Type
          </p>
          <p className="text-lg font-normal leading-7 text-gray-900">
            Please fill up the abstract type information accordingly
          </p>
        </div>

        {/* Form Fields */}
        <div className="flex flex-col w-full space-y-6">
          {/* First Row: Mode and Category */}
          <div className="flex w-full space-x-10">
            {/* Select Presentation Mode */}
            <div className="flex flex-col w-full space-y-1 md:w-1/2">
              <label className="text-sm font-normal leading-5 text-gray-500">
                Select Presentation Mode
              </label>
              <select
                value={selectedMode}
                onChange={handleModeChange}
                className="px-4 py-2 text-sm font-normal leading-5 text-gray-700 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Options</option>
                {modes.map((mode) => (
                  <option key={mode.id} value={mode.id}>
                    {mode.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Select Abstract Category - Only show when mode is selected */}
            {selectedMode && (
              <div className="flex flex-col w-full space-y-1 md:w-1/2">
                <label className="text-sm font-normal leading-5 text-gray-500">
                  Select Abstract Category
                </label>
                <select
                  value={selectedCategory}
                  onChange={handleCategoryChange}
                  className="px-4 py-2 text-sm font-normal leading-5 text-gray-700 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Options</option>
                  {availableCategories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* Second Row: Topic - Only show when category is selected */}
          {selectedCategory && (
            <div className="flex flex-col w-full pr-0 space-y-1 md:w-1/2 md:pr-5">
              <label className="text-sm font-normal leading-5 text-gray-500">
                Select Abstract Topic
              </label>
              <select
                value={selectedTopic}
                onChange={handleTopicChange}
                className="px-4 py-2 text-sm font-normal leading-5 text-gray-700 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Options</option>
                {availableTopics.map((topic) => (
                  <option key={topic.id} value={topic.id}>
                    {topic.name}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* IUPAC Poster Award Participation */}
        {selectedMode && selectedMode === '2' && selectedTopic && (
          <div className="flex flex-col w-full pr-0 space-y-1 md:w-1/2 md:pr-5">
            <p className="text-lg font-bold leading-8 text-gray-900">
              IUPAC Poster Award
            </p>
            <label htmlFor="paParticipation" className="text-sm font-normal leading-5 text-gray-700">
              Do you want to participate in the IUPAC Poster Award? <a href="/assets/abstract/IUPAC_Poster_Award.png" target="_blank" rel="noopener noreferrer" className="italic text-blue-600">Read more</a>
            </label>
            <p className="text-sm font-normal leading-5 text-gray-500">
              *This award is open to all students; Registration with full payment is required to confirm your participation.
            </p>
            <div className="flex items-center mb-4 space-x-2">
              <input
                type="checkbox"
                id="paParticipation"
                checked={posterAward.paParticipation || false}
                onChange={(e) => {
                  const isChecked = e.target.checked
                  setPosterAward({
                    paParticipation: isChecked,
                    paAge: null,
                    paProofOfIdentity: null,
                  })
                  setFormData({
                    ...formData,
                    posterAward: {
                      paParticipation: isChecked,
                      paAge: null,
                      paProofOfIdentity: null,
                    },
                  })
                }}
                className="w-4 h-4"
                style={{ accentColor: 'red' }}
              />
              <p className="text-sm font-normal leading-5 text-red-700">
                IUPAC Poster Award
              </p>
            </div>
            {posterAward.paParticipation && (
              <div className="flex flex-col space-y-4">
                {/* Age Group Input */}
                <div className="flex flex-col space-y-1">
                  <label htmlFor="paAge" className="text-sm font-normal leading-5 text-gray-500">
                    Age
                  </label>
                  <input
                    type="number"
                    id="paAge"
                    value={posterAward.paAge || ''}
                    onChange={(e) => {
                      const value = e.target.value
                      setPosterAward({ ...posterAward, paAge: value })
                      setFormData({
                        ...formData,
                        posterAward: { ...posterAward, paAge: value },
                      })
                    }}
                    className="px-4 py-2 text-sm font-normal leading-5 text-gray-700 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* Proof of Identity Upload */}
                <div className="flex flex-col space-y-1">
                  <label htmlFor="paProofOfIdentity" className="text-sm font-normal leading-5 text-gray-500">
                    Upload Proof of Identity <i>(Student ID Card or letter from the Institution)</i>
                  </label>
                  <input
                    type="file"
                    id="paProofOfIdentity"
                    accept=".pdf,.jpg,.png"
                    onChange={(e) => {
                      const file = e.target.files[0]
                      setPosterAward({ ...posterAward, paProofOfIdentity: file })
                      setFormData({
                        ...formData,
                        posterAward: { ...posterAward, paProofOfIdentity: file },
                      })
                    }}
                    className="px-4 py-2 text-sm font-normal leading-5 text-gray-700 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            )}
          </div>
        )}

              {/* Action Buttons */}
        <div className="flex items-center justify-between w-full">
          <button
            onClick={handleBack}
            className="flex items-center px-6 py-2 transition-colors bg-blue-100 rounded cursor-pointer hover:bg-blue-200"
          >
            <div className="flex items-center space-x-2">
              <span className="text-sm font-normal leading-5 text-gray-900">
                ← Back to Dashboard
              </span>
            </div>
          </button>

          <button
            onClick={handleNext}
            disabled={isNextDisabled}
            className="flex items-center px-6 py-2 transition-colors bg-blue-900 rounded cursor-pointer hover:bg-blue-800 disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            <div className="flex items-center space-x-2">
              <span className="text-sm font-normal leading-5 text-white">
                Author / Co-authors →
              </span>
            </div>
          </button>
        </div>
      </div>
    </div>
  )
}

export default Step1
